import { useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useIntl } from "react-intl";
import type { FormatOptionLabelMeta, InputActionMeta } from "react-select";

import { useGetAccountMappingPaymentLocations } from "api/queries/user/useGetAccountMappingPaymentLocations";
import { ReactComponent as TrashIcon } from "assets/images/iconsV2/trash-01.svg";
import Button from "components/v2/Button/Button";
import Input from "components/v2/Input/Input";
import FormSelect from "components/v2/Select/FormSelect";
import type { SelectOption } from "components/v2/Select/types";
import useDebounce from "hooks/useDebounce";
import { formatPaymentLocationPrimaryLabel, toPaymentLocationOptions } from "./helpers";
import type { AccountMappingFormValues, PaymentLocationOptionsSource } from "./types";

const SEARCH_DEBOUNCE_MS = 400;

function formatPaymentLocationOptionLabel(option: SelectOption, meta: FormatOptionLabelMeta<SelectOption>) {
  if (meta.context === "value" || !option.supportingText) {
    return option.label;
  }

  return (
    <div>
      <div className="cb-select__option-primary">{option.label}</div>
      <div className="cb-select__option-supporting-text account-mapping-option-secondary">{option.supportingText}</div>
    </div>
  );
}

interface AccountMappingRowFieldsProps {
  index: number;
  showLabels: boolean;
  paymentLocationOptions: PaymentLocationOptionsSource;
  selectedPaymentLocations: string[];
  onDelete: () => void;
}

export default function AccountMappingRowFields({
  index,
  showLabels,
  paymentLocationOptions,
  selectedPaymentLocations,
  onDelete,
}: AccountMappingRowFieldsProps) {
  const intl = useIntl();
  const { setValue, watch } = useFormContext<AccountMappingFormValues>();
  const currentPaymentLocation = watch(`mappings.${index}.payment_location`);
  const currentShipperName = watch(`mappings.${index}.shipper_name`);
  const currentLocationName = watch(`mappings.${index}.location_name`);

  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebounce(searchInput, SEARCH_DEBOUNCE_MS);
  const isSearching = Boolean(debouncedSearch);

  const searchedOptions = useGetAccountMappingPaymentLocations({ query: debouncedSearch }, { enabled: isSearching });
  const activeOptions = isSearching ? searchedOptions : paymentLocationOptions;
  const { paymentLocations, isLoading, isFetching, isFetchingNextPage, hasNextPage, fetchNextPage } = activeOptions;

  const options: SelectOption[] = useMemo(() => {
    const fetched = toPaymentLocationOptions(paymentLocations).map((option) => ({
      ...option,
      isDisabled: selectedPaymentLocations.includes(option.value) && option.value !== currentPaymentLocation,
    })) as SelectOption[];

    if (currentPaymentLocation && !fetched.some((option) => option.value === currentPaymentLocation)) {
      fetched.unshift({
        label: formatPaymentLocationPrimaryLabel({
          location_name: currentLocationName,
          payment_location: currentPaymentLocation,
        }),
        value: currentPaymentLocation,
        supportingText: currentShipperName?.trim() || undefined,
      });
    }

    return fetched;
  }, [paymentLocations, selectedPaymentLocations, currentPaymentLocation, currentLocationName, currentShipperName]);

  const handleInputChange = (value: string, meta: InputActionMeta) => {
    if (meta.action === "input-change") {
      setSearchInput(value);
      return;
    }

    if (meta.action === "menu-close") {
      setSearchInput("");
    }
  };

  return (
    <>
      <div className="account-mapping-row-fields">
        <div className="account-mapping-col-shipper">
          <FormSelect
            name={`mappings.${index}.payment_location`}
            label={
              showLabels ? intl.formatMessage({ id: "settings.accountMapping.shipper", defaultMessage: "Shipper" }) : ""
            }
            placeholder={intl.formatMessage({ id: "general.search", defaultMessage: "Search" })}
            options={options}
            required
            saveOnChange={false}
            filterOption={() => true}
            isLoading={isLoading || (isFetching && !isFetchingNextPage && paymentLocations.length === 0)}
            formatOptionLabel={formatPaymentLocationOptionLabel}
            isOptionDisabled={(option: any) => Boolean((option as SelectOption & { isDisabled?: boolean }).isDisabled)}
            onInputChange={handleInputChange}
            onMenuScrollToBottom={() => {
              if (hasNextPage && !isFetchingNextPage) {
                fetchNextPage();
              }
            }}
            onChange={(value) => {
              const location = paymentLocations.find((item) => item.payment_location === value);
              if (!location) {
                return;
              }
              setValue(`mappings.${index}.shipper_name`, location.shipper_name ?? "");
              setValue(`mappings.${index}.location_name`, location.location_name ?? "");
            }}
          />
        </div>
        <div className="account-mapping-col-value">
          <Input
            name={`mappings.${index}.external_account_number`}
            label={
              showLabels ? intl.formatMessage({ id: "settings.accountMapping.yourValue", defaultMessage: "Your value" }) : ""
            }
            required
            saveOnBlur={false}
            registerOptions={{
              validate: (value: string) =>
                Boolean(value?.trim()) ||
                intl.formatMessage({ id: "general.requiredField", defaultMessage: "This is a required field." }),
            }}
          />
        </div>
      </div>
      <div className="account-mapping-row-actions">
        <Button
          testId="account-mapping-delete"
          variant="tertiary-gray"
          size="sm"
          aria-label={intl.formatMessage({ id: "general.delete", defaultMessage: "Delete" })}
          iconLeft={<TrashIcon />}
          onClick={onDelete}
        />
      </div>
    </>
  );
}
